import React, { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  useDeleteArtistArtworkMutation,
  useGetArtistPageQuery,
  useToggleStandalonePageMutation,
} from "../../services/rtkApi/artistsGalleryApi";
import { useAppSelector } from "../../store/hooks";
import { ArtistArtworkCard } from "./ArtistArtworkCard";
import { ArtistsGalleryFontLinks } from "./ArtistsGalleryFontLinks";
import { artistsGalleryFontFamily } from "./artistsGalleryFont";
import { ContributorsStrip } from "./ContributorsStrip";
import type { ArtistArtwork } from "./types";

function buildStandaloneUrl(id: string): string {
  // standalone page is rendered by the frontend SPA at /artworks/:id
  return `/artworks/${id}`;
}

function roleHas(roleStr: string | null | undefined, target: string): boolean {
  if (!roleStr) return false;
  return roleStr
    .split(",")
    .map((r) => r.trim())
    .includes(target);
}

const ArtistPage: React.FC = () => {
  const { username = "" } = useParams<{ username: string }>();
  const { data, isLoading, error } = useGetArtistPageQuery(username, {
    skip: !username,
  });
  const myUsername = useAppSelector((s) => s.auth.username);
  const viewerRole = useAppSelector((s) => s.auth.role);
  const navigate = useNavigate();
  const [deleteArtwork] = useDeleteArtistArtworkMutation();
  const [toggleStandalone] = useToggleStandalonePageMutation();

  const isAdminUser = roleHas(viewerRole, "admin");

  // server is the source of truth — populated as part of /by-artist response
  const viewerCanEdit = !!data?.viewerCanEdit;
  const isOwn = myUsername === username;

  useEffect(() => {
    if (data?.artist?.displayName) {
      document.title = `${data.artist.displayName} · n450s`;
    }
  }, [data?.artist?.displayName]);

  const handleDelete = async (artwork: ArtistArtwork) => {
    const confirmed = window.confirm(`Delete "${artwork.title}"? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await deleteArtwork(artwork._id).unwrap();
      toast.success("Deleted", { position: "bottom-right" });
    } catch (err: any) {
      toast.error(err?.data?.message || "Delete failed", {
        position: "bottom-right",
      });
    }
  };

  const handle = data?.artist
    ? `${data.artist.userEmoji ? `${data.artist.userEmoji}.` : ""}${data.artist.username}`
    : "";

  return (
    <Page>
      <ArtistsGalleryFontLinks />
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"
      />

      <BackRow>
        <BackLink to="/art">
          <i className="fa fa-angle-left" aria-hidden="true"></i> back to the main gallery
        </BackLink>
      </BackRow>

      {isLoading && <Notice>loading…</Notice>}
      {error && <Gate>artist not found.</Gate>}

      {data && (
        <>
          <Header>
            <ArtistHeadingBlock>
              <Title>{handle}'s hosted gallery</Title>
              {data.artist.displayName &&
                data.artist.displayName !== data.artist.username && (
                  <DisplayNameLine>{data.artist.displayName}</DisplayNameLine>
                )}
              {data.artist.bio && <BioLine>{data.artist.bio}</BioLine>}
            </ArtistHeadingBlock>
            <RightSlot>
              <ContributorsStrip activeUsername={username} />
            </RightSlot>
          </Header>

          {isOwn && (
            <ActionRow>
              <ActionLink to="/art/contribute">
                <i className="fa fa-plus" aria-hidden="true"></i> add artwork
              </ActionLink>
            </ActionRow>
          )}

          {data.artworks.length === 0 && (
            <EmptyBox>
              {isOwn
                ? "you haven't added any artwork yet. press add artwork to submit your first piece."
                : "no artworks yet from this artist."}
            </EmptyBox>
          )}

          <Grid>
            {data.artworks.map((artwork) => (
              <CardWrap key={artwork._id}>
                <ArtistArtworkCard artwork={artwork} />
                {viewerCanEdit && (
                  <CardToolbar>
                    <TextAction
                      type="button"
                      onClick={() => navigate(`/art/edit/${artwork._id}`)}
                    >
                      edit
                    </TextAction>
                    <ToolbarSep aria-hidden>
                      ·
                    </ToolbarSep>
                    <TextAction type="button" onClick={() => handleDelete(artwork)}>
                      delete
                    </TextAction>
                    <ToolbarSep aria-hidden>
                      ·
                    </ToolbarSep>
                    <TextLink
                      href={buildStandaloneUrl(artwork._id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="open the standalone, archive-friendly page for this artwork"
                      onClick={(e) => e.stopPropagation()}
                    >
                      standalone page
                    </TextLink>
                    {isAdminUser && (
                      <>
                        <ToolbarSep aria-hidden>
                          ·
                        </ToolbarSep>
                        <TextAction
                          type="button"
                          onClick={() =>
                            toggleStandalone({
                              id: artwork._id,
                              enabled: !artwork.standalonePageEnabled,
                            })
                          }
                          title={
                            artwork.standalonePageEnabled
                              ? "admin: turn off public link (page still exists, noindex)"
                              : "admin: turn on public link (indexed / discoverable)"
                          }
                        >
                          {artwork.standalonePageEnabled
                            ? "public link: on"
                            : "public link: off"}
                        </TextAction>
                      </>
                    )}
                  </CardToolbar>
                )}
              </CardWrap>
            ))}
          </Grid>
        </>
      )}
    </Page>
  );
};

const Page = styled.div`
  background-color: #b286ffff;
  padding: 45px;
  min-height: 100vh;
  position: relative;
  font-family: ${artistsGalleryFontFamily};

  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const BackRow = styled.div`
  margin-bottom: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const ArtistHeadingBlock = styled.div`
  flex: 1 1 auto;
  min-width: min(100%, 12rem);
`;

const RightSlot = styled.div`
  display: flex;
  align-items: baseline;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  font-weight: 700;
  word-break: break-word;
`;

const DisplayNameLine = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  margin-top: 4px;
  opacity: 0.82;
`;

const BioLine = styled.div`
  font-size: 0.9rem;
  font-family: inherit;
  font-weight: 500;
  margin-top: 8px;
  max-width: 56ch;
  line-height: 1.35;
`;

const ActionRow = styled.div`
  margin-bottom: 20px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  color: #000000;
  text-decoration: none;
  background: #ffffff;
  border: 4px solid #000000;
  padding: 6px 12px;
  box-shadow: 5px 8px rgba(0, 0, 0, 0.7);
  transition: transform 0.1s ease;

  &:hover {
    transform: translate(1px, 1px);
    box-shadow: 4px 7px rgba(0, 0, 0, 0.7);
    color: #000000;
  }

  .fa {
    font-size: 15px;
  }
`;

const ActionLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  color: #000000;
  text-decoration: none;
  background: #fff77a;
  border: 4px solid #000000;
  padding: 6px 12px;
  box-shadow: 5px 8px rgba(0, 0, 0, 0.7);
  transition: transform 0.1s ease;

  &:hover {
    transform: translate(1px, 1px);
    box-shadow: 4px 7px rgba(0, 0, 0, 0.7);
    color: #000000;
  }
`;

const Notice = styled.div`
  background: #fff77a;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  padding: 8px 12px;
  margin-bottom: 16px;
  font-weight: 600;
`;

const Gate = styled.div`
  background: #ffd1f0;
  border: 3px solid #000000;
  box-shadow: 3px 4px rgba(0, 0, 0, 0.7);
  padding: 12px 14px;
  margin-bottom: 16px;
`;

const EmptyBox = styled.div`
  background: #ffffff;
  border: 3px dashed #000000;
  padding: 18px 16px;
  margin-bottom: 24px;
  font-weight: 500;
`;

const Grid = styled.div`
  display: grid;
  margin-top: 10px;
  grid-gap: 4rem;
  grid-template-columns: repeat(auto-fit, minmax(18rem, 0.5fr));

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const CardWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  /* match /artists listing: centered only on narrow viewports */
  @media screen and (max-width: 600px) {
    align-items: center;
  }
`;

const CardToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin-top: 4px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #000000;
`;

const ToolbarSep = styled.span`
  user-select: none;
  opacity: 0.45;
`;

const textActionReset = `
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const TextAction = styled.button`
  ${textActionReset}
`;

const TextLink = styled.a`
  ${textActionReset}
`;

export default ArtistPage;
